import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Breadcrumb } from "@/components/Breadcrumb";

import heroImage from "@/assets/patio-paver-livingston-essex-county-nj.webp";
import paverPatioImg from "@/assets/paver-patio-installation-livingston-essex-county-nj.webp";
import paverDrivewayImg from "@/assets/driveways/paver-driveway-installation-cedar-grove-nj-2.webp";
import paverWalkwayImg from "@/assets/walkways/paver-walkway-steps-installation-north-caldwell-nj-2.webp";
import paverPatioRepairImg from "@/assets/paver-patio-repair-totowa-passaic-county-new-jersey-2.webp";

const sections = [
  {
    title: "Paver Patio Installation",
    highlight: "in North Jersey",
    content: [
      "A paver patio can add real usable space to your backyard. It's one of the most common projects we handle for families, homeowners who like to entertain, and anyone looking to replace an old wood deck or a bare yard.",
      "The process starts with base preparation. Crews excavate the area, compact a layered gravel base, and set edge restraints before laying pavers in the pattern you choose. Joints get filled with polymeric sand to lock everything in place. Most residential patios take a few days from start to finish.",
      <>
        Bergen County and Passaic County homes often sit on clay-heavy soil. That type of ground holds water and can shift if the base isn't graded properly. We account for that during prep so the patio drains the way it should and stays level after heavy rain. See how our <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">paver patio installation</Link> process works, from excavation to the last joint.
      </>,
    ],
    link: "/paving-services/paver-patios",
    linkText: "See how we build a paver patio",
    image: paverPatioImg,
    imageAlt: "Paver patio installation in Livingston, Essex County, New Jersey",
  },
  {
    title: "Paver Driveway Installation",
    highlight: "in North Jersey",
    content: [
      "If your current driveway is cracked, stained, or just worn out, pavers can give you a surface that handles daily vehicle traffic and holds up through our winters. Interlocking pavers flex with ground movement instead of cracking like a poured slab, and a single damaged unit can be swapped out without redoing the whole driveway.",
      <>
        We handle full driveway installations across Passaic, Bergen, Essex, and Morris counties, from excavation and base prep through final compaction. Each project is built to ICPI standards with a heavy-duty gravel base sized for vehicle loads and local soil conditions. Here's what <Link to="/paving-services/paver-driveways" className="text-secondary hover:underline">paver driveway installation</Link> involves, from base depth to edge restraint.
      </>,
    ],
    link: "/paving-services/paver-driveways",
    linkText: "What a paver driveway install involves",
    image: paverDrivewayImg,
    imageAlt: "Paver driveway installation in Cedar Grove, Essex County, New Jersey",
  },
  {
    title: "Paver Walkway Installation",
    highlight: "in North Jersey",
    content: [
      "Whether you're improving the path to your front door, connecting a patio to a pool area, or replacing a crumbling concrete walkway, pavers can give you a level surface with clean edges. They also work well on properties with uneven terrain that need a stable walking path.",
      "Crews excavate the area, compact a gravel and sand base, and lay the pavers in a running bond or herringbone pattern. Most walkway projects wrap up in one to two days, depending on the length and layout.",
      <>
        Essex County properties with mature trees often deal with root heave that pushes up and cracks old concrete paths. Pavers can adjust to minor ground shifts better than a single poured slab, which makes them a practical fit for yards with large root systems. Learn more about <Link to="/paving-services/paver-walkways" className="text-secondary hover:underline">paver walkway installation</Link> for front entries and side yards.
      </>,
    ],
    link: "/paving-services/paver-walkways",
    linkText: "More on paver walkway installation",
    image: paverWalkwayImg,
    imageAlt: "Paver walkway and steps installation in North Caldwell, Essex County, New Jersey",
  },
  {
    title: "Paver Patio Repair",
    highlight: "in North Jersey",
    content: [
      "If your paver patio has shifted, settled, or started growing weeds through the joints, it may not need a full replacement. A lot of the time, the fix is more targeted than you'd expect.",
      "Repair work typically involves lifting the affected pavers, re-leveling the sand bed underneath, replacing any cracked units, and re-applying polymeric sand to lock the joints back down. Most repairs can be finished in a single visit without tearing out the entire patio.",
      <>
        North Jersey winters push pavers out of alignment over time. That's just what happens with repeated freezing and thawing. Spring tends to be the busiest season for patio restoration across Bergen County and the surrounding area, so it's worth reaching out early if you notice any movement. If your patio is sinking or shifting, <Link to="/paving-services/paver-patio-repair" className="text-secondary hover:underline">paver patio repair</Link> can often save it without a full rebuild.
      </>,
    ],
    link: "/paving-services/paver-patio-repair",
    linkText: "How we fix a sinking paver patio",
    image: paverPatioRepairImg,
    imageAlt: "Paver patio repair in Totowa, Passaic County, New Jersey",
  },
];

const PavingServices = () => {
  return (
    <>
      <Helmet>
        <title>Paving Contractor Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Paver patios, concrete driveways, walkways, and sidewalks for North Jersey homes. Local crews serving Passaic, Bergen, Essex, and Morris Counties daily." />
        <meta property="og:description" content="Paver patios, concrete driveways, walkways, and sidewalks for North Jersey homes. Local crews serving Passaic, Bergen, Essex, and Morris Counties daily." />
        
        <meta property="og:image" content="https://masonry-pro.com/og/paving-services.jpg" />
        <meta property="og:title" content="Paving Contractor Totowa, NJ | Masonry Pro Construction" />
        <meta name="twitter:title" content="Paving Contractor Totowa, NJ | Masonry Pro Construction" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/paving-services.jpg" />
      </Helmet>

      <Header />
      <Breadcrumb />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
          <img src={heroImage} alt="Paver installation services in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
              Local Paving Contractor in <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Totowa, NJ</span> Serving North Jersey
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.25rem', maxWidth: '48rem', margin: '0 auto 2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
              Paver patios, walkways, and driveways that hold up through North Jersey seasons
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="xl" asChild>
                <a href="tel:+19737252395" className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  (973) 725-2395
                </a>
              </Button>
              <Button variant="goldOutline" size="xl" asChild>
                <a href="#contact">Get Free Estimate</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Driveways, Patios, Walkways & <span className="text-gradient-gold">Sidewalks</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              The weather in North Jersey can take its toll on your driveway, patio, and walkways. The freeze-thaw cycles can crack concrete. The rain can pool on uneven surfaces, and after a few winters, what looked fine begins to shift and settle. We handle paving projects built for New Jersey weather, local drainage conditions, and municipal code requirements. Paving is one part of what we do as a <Link to="/" className="text-secondary hover:underline">North Jersey masonry and hardscape contractor</Link>, so the same crew that sets your pavers also understands the walls and steps around them.
            </p>
          </div>
        </section>

        {/* Service Sections — alternating image/content */}
        {sections.map((section, index) => {
          const isEven = index % 2 === 0;
          return (
            <section
              key={section.title}
              className={`py-16 md:py-20 ${index % 2 === 0 ? "bg-muted/30" : "bg-background"}`}
            >
              <div className="container mx-auto px-4">
                <div
                  className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${
                    !isEven ? "lg:[direction:rtl]" : ""
                  }`}
                >
                  {/* Content */}
                  <div className={!isEven ? "lg:[direction:ltr]" : ""}>
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                      {section.title} <span className="text-gradient-gold">{section.highlight}</span>
                    </h2>
                    <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                      {section.content.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                    <Link
                      to={section.link}
                      className="inline-flex items-center gap-2 text-secondary font-medium hover:gap-3 transition-all duration-300 mt-4"
                    >
                      {section.linkText} <ArrowRight className="h-5 w-5" />
                    </Link>
                  </div>

                  {/* Image */}
                  <div
                    className={`overflow-hidden rounded-xl aspect-[4/3] ${
                      !isEven ? "lg:[direction:ltr]" : ""
                    }`}
                  >
                    <img
                      src={section.image}
                      alt={section.imageAlt}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* YouTube Shorts */}
        <YouTubeShortsSection
          videoIds={["IaLfa_u0pFQ", "awvHabWeQFw", "p_RCDyOyCgU"]}
          title="Watch Our"
          highlightedTitle="Paving Work"
        />

        <RelatedServices currentCategory="paving" />

        {/* CTA Section */}
        <CTASection serviceName="paving" heading="Need a" highlightedHeading="Paving Estimate?" />
      </main>

      <Footer />
    </>
  );
};

export default PavingServices;
